import { useCallback } from 'react';
import {
  Button, Collapse, Typography, Form, Select, Input, Slider,
  Row, Col, InputNumber
} from 'antd';
import {
  BoldOutlined, ItalicOutlined, UnderlineOutlined,
} from '@ant-design/icons';
import { useCanvas } from '../../context/canvasContext';
import { useParams } from "react-router-dom";
import { useDocument } from '../../context/documentInfoContex';
import { boardServices } from '../../api';
import { debounce } from 'lodash';

const { Panel } = Collapse;
const { Option } = Select;
const { Title } = Typography;

const TextMenu = (props) => {
  const {
    textColor, setTextColor,
    textSize, setTextSize,
    textFont, setTextFont,
    textAlign, setTextAlign,
    textStyle, setTextStyle,
    setTextMenuVisible,
    isTextMenuVisible,
  } = props;

  const { id } = useParams();
  const { canvasRef } = useCanvas();
  const { setSavingData } = useDocument();
  const isMobile = window.screen.width < 768;

  const handleTextColorChange = (e) => {
    const activeObject = canvasRef.current.getActiveObject();
    if (activeObject && activeObject.isType('i-text')) {
      const color = e.target.value;
      setTextColor(color);
      debouncedApplyTextColor(color);
    }
  };

  const debouncedApplyTextColor = useCallback(
    debounce((color) => {
      const activeObject = canvasRef.current.getActiveObject();
      if (activeObject && activeObject.isType('i-text')) {
        activeObject.set('fill', color);
        canvasRef.current.renderAll();
        const data = canvasRef.current.toJSON(["selectable", "id", "type"]);
        boardServices.updateBoardData(id, data, setSavingData);
      }
    }, 300),
    [canvasRef, id, setSavingData]
  );

  const debouncedApplyTextSize = useCallback(
    debounce((value) => {
      const activeObject = canvasRef.current.getActiveObject();
      if (activeObject && activeObject.isType('i-text')) {
        activeObject.set('fontSize', value);
        canvasRef.current.renderAll();
        const data = canvasRef.current.toJSON(["selectable", "id", "type"]);
        boardServices.updateBoardData(id, data, setSavingData);
      }
    }, 300),
    [canvasRef, id, setSavingData]
  );

  const handleTextSizeChange = (value) => {
    const activeObject = canvasRef.current.getActiveObject();
    if (activeObject && activeObject.isType('i-text')) {
      activeObject.set('fontSize', value);
      canvasRef.current.renderAll();
      setTextSize(value);
      debouncedApplyTextSize(value);
    }
  };

  const handleTextFontChange = (value) => {
    const activeObject = canvasRef.current.getActiveObject();
    if (activeObject && activeObject.isType('i-text')) {
      activeObject.set('fontFamily', value);
      canvasRef.current.renderAll();
      setTextFont(value);
      const data = canvasRef.current.toJSON(["selectable", "id", "type"]);
      boardServices.updateBoardData(id, data, setSavingData);
    }
  };

  const handleTextStyleChange = (styles) => {
    const activeObject = canvasRef.current.getActiveObject();
    if (activeObject && activeObject.isType('i-text')) {
      activeObject.set('fontWeight', styles.includes('bold') ? 'bold' : 'normal');
      activeObject.set('fontStyle', styles.includes('italic') ? 'italic' : 'normal');
      activeObject.set('underline', styles.includes('underline') ? true : false);
      canvasRef.current.renderAll();
      setTextStyle(styles);
      const data = canvasRef.current.toJSON(["selectable", "id", "type"]);
      boardServices.updateBoardData(id, data, setSavingData);
    }
  };

  const toggleStyle = (style) => {
    let newStyles = [...textStyle];
    const index = newStyles.indexOf(style);
    if (index !== -1) {
      newStyles.splice(index, 1);
    } else {
      newStyles.push(style);
    }
    handleTextStyleChange(newStyles);
  };

  const handleCollapseChange = (activeKeys) => {
    const isVisible = activeKeys.includes('1');
    setTextMenuVisible(isVisible);
  };


  return (
    <Collapse
      bordered={true}
      defaultActiveKey={['1']}
      expandIconPosition="right"
      activeKey={isTextMenuVisible ? ['1'] : []}
      onChange={handleCollapseChange}
      style={{
        width: isMobile ? '125px' : '168px',
        position: isMobile ? 'absolute' : '',
        top: isMobile ? '-620px' : '',
        right: isMobile ? '135px' : '',
      }}
    >
      <Panel header="Text Properties" key="1" style={{ fontSize: isMobile ? '11px' : '16px' }}>
        <Title level={5} style={{ fontSize: isMobile ? '11px' : '16px' }}>Text Color</Title>
        <Form.Item>
          <Input
            type="color"
            style={{ width: isMobile ? '80px' : '112px' }}
            value={textColor}
            onChange={handleTextColorChange}
          />
        </Form.Item>

        <Title level={5} style={{ fontSize: isMobile ? '11px' : '16px' }}>Text Size</Title>
        <Form.Item>
          <Row align="middle">
            <Col flex="auto">
              <Slider
                style={{ width: isMobile ? '80px' : '112px' }}
                min={10}
                onChange={handleTextSizeChange}
                value={textSize || 50}
              />
            </Col>
            <Col flex="60px">
              <InputNumber
                min={1}
                value={textSize || 10}
                onChange={handleTextSizeChange}
              />
            </Col>
          </Row>
        </Form.Item>

        <Title level={5} style={{ fontSize: isMobile ? '11px' : '16px' }}>Text Font</Title>
        <Form.Item>
          <Select
            style={{ width: isMobile ? '80px' : '112px' }}
            value={textFont}
            onChange={handleTextFontChange}
          >
            <Option value="Arial">Arial</Option>
            <Option value="Times New Roman">Times New Roman</Option>
            <Option value="Comic Sans MS">Comic Sans MS</Option>
            <Option value="Tahoma">Tahoma</Option>
            <Option value="Verdana">Verdana</Option>
          </Select>
        </Form.Item>

        <Title level={5} style={{ fontSize: isMobile ? '11px' : '16px' }}>Text Style</Title>
        <Form.Item>
          <Button.Group>
            <Button
              style={{ width: isMobile ? '27px' : '42px', padding: isMobile ? '2px 6px' : '4px 15px' }}
              type={textStyle?.includes('bold') ? 'primary' : 'default'}
              onClick={() => toggleStyle('bold')}
            >
              <BoldOutlined />
            </Button>
            <Button
              style={{ width: isMobile ? '27px' : '42px', padding: isMobile ? '2px 6px' : '4px 15px' }}
              type={textStyle?.includes('italic') ? 'primary' : 'default'}
              onClick={() => toggleStyle('italic')}
            >
              <ItalicOutlined />
            </Button>
            <Button
              style={{ width: isMobile ? '27px' : '42px', padding: isMobile ? '2px 6px' : '4px 15px' }}
              type={textStyle?.includes('underline') ? 'primary' : 'default'}
              onClick={() => toggleStyle('underline')}
            >
              <UnderlineOutlined />
            </Button>
          </Button.Group>
        </Form.Item>
      </Panel>
    </Collapse>
  );
};

export default TextMenu;